import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, fonts } from '../theme';
import { devLog, devError } from '../utils/debugLogger';
import { 
  Shield, Search, User, Crown, Heart, Code, 
  Check, X, AlertTriangle, Loader, RefreshCw,
  ArrowLeft, Users, ChevronDown, Zap, Calendar, Mail, BellOff,
  Link2, DollarSign, TrendingUp, Copy, Send, Trash2,
  Square, CheckSquare, Minus
} from 'lucide-react';
import { db, functions, auth } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  getDocs, 
  query, 
  orderBy, 
  where 
} from 'firebase/firestore';
import { TIERS, getAllTierIds, getTierConfig } from '../config/tiers';
import { FUNCTION_URLS } from '../config/functions';
import Footer from '../components/Footer';

// Compact inline button for dev tools
const DevBtn = ({ label, onClick, loading, color, icon: Icon, title, disabled }) => (
  <button
    onClick={onClick}
    disabled={loading || disabled}
    className="px-2 py-1 rounded text-xs font-medium transition-all hover:opacity-80 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
    style={{ backgroundColor: color, color: '#fff' }}
    title={title || label}
  >
    {loading ? <Loader size={12} className="animate-spin" /> : Icon && <Icon size={12} />}
    {label}
  </button>
);

// Result badge for dev tools
const ResultBadge = ({ result, colors }) => result ? (
  <span 
    className="text-xs px-2 py-0.5 rounded flex items-center gap-1"
    style={{ 
      backgroundColor: result.success ? `${colors.success}20` : `${colors.error}20`,
      color: result.success ? colors.success : colors.error
    }}
  >
    {result.success ? <Check size={10} /> : <AlertTriangle size={10} />}
    {result.message?.slice(0, 50)}{result.message?.length > 50 ? '...' : ''}
  </span>
) : null;

// Timezone select options (shared)
const TZ_OPTIONS = [
  { v: '-12', l: 'UTC-12' }, { v: '-11', l: 'UTC-11' }, { v: '-10', l: 'UTC-10 (HI)' },
  { v: '-9', l: 'UTC-9 (AK)' }, { v: '-8', l: 'UTC-8 (PST)' }, { v: '-7', l: 'UTC-7 (MST)' },
  { v: '-6', l: 'UTC-6 (CST)' }, { v: '-5', l: 'UTC-5 (EST)' }, { v: '-4', l: 'UTC-4' },
  { v: '-3', l: 'UTC-3' }, { v: '0', l: 'UTC+0' }, { v: '1', l: 'UTC+1' },
  { v: '5.5', l: 'UTC+5:30' }, { v: '8', l: 'UTC+8' }, { v: '9', l: 'UTC+9' }, { v: '10', l: 'UTC+10' }
];

// Compact DevTools Panel - replaces TestButton, RevokeTokensForm, EmailNotificationManager
const DevToolsPanel = ({ colors, user }) => {
  // Revoke tokens state
  const [revokeUid, setRevokeUid] = useState('');
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeResult, setRevokeResult] = useState(null);
  
  // Email notification state
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState(null);
  const [targetUid, setTargetUid] = useState('');
  const [timezoneOffset, setTimezoneOffset] = useState('-5');
  const [individualTimezone, setIndividualTimezone] = useState('-5');
  
  // Google Calendar test state
  const [gcalLoading, setGcalLoading] = useState(null);
  
  // Handlers
  const handleRevokeTokens = async () => {
    if (!revokeUid.trim()) return;
    setRevokeLoading(true);
    setRevokeResult(null);
    try {
      const revokeFn = httpsCallable(functions, 'revokeUserGoogleTokens');
      const result = await revokeFn({ targetUserId: revokeUid.trim() });
      setRevokeResult({ success: true, message: result.data.message });
      setRevokeUid('');
    } catch (error) {
      setRevokeResult({ success: false, message: error.message });
    } finally {
      setRevokeLoading(false);
    }
  };
  
  const emailAction = async (action, endpoint, method = 'POST', body = {}) => {
    setLoading(action);
    setResult(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(endpoint, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');
      setResult({ success: true, message: data.message || 'Success' });
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setLoading(null);
    }
  };
  
  const gcalTest = async (type, fn) => {
    setGcalLoading(type);
    try {
      await fn();
    } finally {
      setGcalLoading(null);
    }
  };
  
  return (
    <div className="space-y-3 text-xs">
      {/* Google Calendar Tests - single row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium flex items-center gap-1" style={{ color: colors.textSecondary }}>
          <Calendar size={12} /> GCal:
        </span>
        <DevBtn
          label="Sync"
          color={colors.accent}
          icon={RefreshCw}
          loading={gcalLoading === 'sync'}
          onClick={() => gcalTest('sync', async () => {
            try {
              const syncFn = httpsCallable(functions, 'syncGoogleCalendar');
              await syncFn();
              alert('✅ Sync OK');
            } catch (e) { alert(`❌ ${e.message}`); }
          })}
          title="Test token refresh sync"
        />
        <DevBtn
          label="Create"
          color={colors.success}
          icon={Calendar}
          loading={gcalLoading === 'create'}
          onClick={() => gcalTest('create', async () => {
            try {
              const createFn = httpsCallable(functions, 'createGoogleEvent');
              await createFn({ summary: `Test ${new Date().toLocaleTimeString()}`, startDate: new Date().toISOString().split('T')[0], isAllDay: true, calendarId: 'primary' });
              alert('✅ Created');
            } catch (e) { alert(`❌ ${e.message}`); }
          })}
          title="Create test event"
        />
        <DevBtn
          label="Token"
          color={colors.textSecondary}
          icon={Shield}
          loading={gcalLoading === 'token'}
          onClick={() => gcalTest('token', async () => {
            try {
              const secretDoc = await getDoc(doc(db, 'users', user.userId, 'secrets', 'google'));
              if (secretDoc.exists()) {
                const d = secretDoc.data();
                alert(`Access: ${d.accessToken ? '✅' : '❌'} | Refresh: ${d.refreshToken ? '✅' : '❌'} | Exp: ${d.expiryDate ? new Date(d.expiryDate).toLocaleString() : 'N/A'}`);
              } else alert('❌ No tokens');
            } catch (e) { alert(`❌ ${e.message}`); }
          })}
          title="Check token status"
        />
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" 
           className="text-xs underline" style={{ color: colors.textMuted }}>Revoke@Google</a>
      </div>
      
      {/* Revoke User Tokens - single row */}
      <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: colors.border }}>
        <span className="font-medium flex items-center gap-1" style={{ color: colors.textSecondary }}>
          <Shield size={12} /> Revoke:
        </span>
        <input
          type="text"
          value={revokeUid}
          onChange={(e) => setRevokeUid(e.target.value)}
          placeholder="User UID"
          className="px-2 py-1 rounded text-xs outline-none w-48"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textPrimary }}
        />
        <DevBtn label="Revoke" color="#c0503f" icon={Trash2} loading={revokeLoading} onClick={handleRevokeTokens} disabled={!revokeUid.trim()} />
        <ResultBadge result={revokeResult} colors={colors} />
      </div>
      
      {/* Email Controls - two rows */}
      <div className="pt-2 border-t space-y-2" style={{ borderColor: colors.border }}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium flex items-center gap-1" style={{ color: colors.textSecondary }}>
            <Mail size={12} /> Email:
          </span>
          <DevBtn label="Enable All" color={colors.success} icon={Mail} loading={loading === 'enable'} 
            onClick={() => emailAction('enable', FUNCTION_URLS.notifications.update, 'POST', { enabled: true })} />
          <DevBtn label="Disable All" color="#c0503f" icon={BellOff} loading={loading === 'disable'} 
            onClick={() => emailAction('disable', FUNCTION_URLS.notifications.update, 'POST', { enabled: false })} />
          <DevBtn label="Backfill" color={colors.accent} icon={Users} loading={loading === 'backfill'} 
            onClick={() => emailAction('backfill', FUNCTION_URLS.notifications.backfill)} />
          <DevBtn label="Test Digest" color={colors.accent} icon={Send} loading={loading === 'testDigest'} 
            onClick={() => emailAction('testDigest', FUNCTION_URLS.notifications.testDigest)} />
          <DevBtn label="Send All" color="#f97316" icon={Send} loading={loading === 'sendAll'} 
            onClick={() => emailAction('sendAll', FUNCTION_URLS.notifications.sendDigestToAll)} title="⚠️ Send to ALL users" />
          <DevBtn label="Nudge" color={colors.textSecondary} icon={Mail} loading={loading === 'nudge'} 
            onClick={() => emailAction('nudge', FUNCTION_URLS.notifications.testNudge)} />
          <ResultBadge result={result} colors={colors} />
        </div>
        
        {/* Timezone bulk + individual on same row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium" style={{ color: colors.textSecondary }}>TZ:</span>
          <select value={timezoneOffset} onChange={(e) => setTimezoneOffset(e.target.value)}
            className="px-2 py-1 rounded text-xs outline-none"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textPrimary }}>
            {TZ_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <DevBtn label="Set Default" color={colors.accent} loading={loading === 'bulkTz'} 
            onClick={() => emailAction('bulkTz', FUNCTION_URLS.timezone.setDefault, 'POST', { offset: parseFloat(timezoneOffset) })} />
          <span className="text-xs" style={{ color: colors.textMuted }}>|</span>
          <input type="text" value={targetUid} onChange={(e) => setTargetUid(e.target.value)} placeholder="UID"
            className="px-2 py-1 rounded text-xs outline-none w-32"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textPrimary }} />
          <select value={individualTimezone} onChange={(e) => setIndividualTimezone(e.target.value)}
            className="px-2 py-1 rounded text-xs outline-none"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textPrimary }}>
            {TZ_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <DevBtn label="Set" color={colors.accent} loading={loading === 'individualTz'} disabled={!targetUid.trim()}
            onClick={() => emailAction('individualTz', FUNCTION_URLS.timezone.setUserTimezone, 'POST', { userId: targetUid, offset: parseFloat(individualTimezone) })} />
          <DevBtn label="✓" color={colors.success} loading={loading === 'enableUser'} disabled={!targetUid.trim()}
            onClick={() => emailAction('enableUser', FUNCTION_URLS.notifications.update, 'POST', { userId: targetUid, enabled: true })} title="Enable notifications" />
          <DevBtn label="✕" color="#c0503f" loading={loading === 'disableUser'} disabled={!targetUid.trim()}
            onClick={() => emailAction('disableUser', FUNCTION_URLS.notifications.update, 'POST', { userId: targetUid, enabled: false })} title="Disable notifications" />
        </div>
      </div>
    </div>
  );
};

// Affiliate Stats component
const AffiliateStatsPanel = ({ colors }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(FUNCTION_URLS.affiliate.getStats, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
      devLog('[Admin] Affiliate stats:', data);
    } catch (err) {
      devError('[Admin] Failed to fetch affiliate stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyAffiliateLink = (code) => {
    const link = `https://my-mind.me/?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(code);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Fetch Button */}
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: colors.textSecondary }}>
          View subscription affiliates from Stripe
        </p>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 disabled:opacity-50 flex items-center gap-2"
          style={{ 
            backgroundColor: colors.accent,
            color: colors.onAccent
          }}
        >
          {loading ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <>
              <RefreshCw size={14} />
              Fetch Stats
            </>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div 
          className="p-3 rounded-lg text-sm flex items-center gap-2"
          style={{ backgroundColor: `${colors.error}20`, color: colors.error }}
        >
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div 
              className="p-3 rounded-md text-center"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <div className="text-2xl font-bold" style={{ color: colors.accent }}>
                {stats.summary.totalSubscriptions}
              </div>
              <div className="text-xs" style={{ color: colors.textMuted }}>Total Subs</div>
            </div>
            <div 
              className="p-3 rounded-md text-center"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <div className="text-2xl font-bold" style={{ color: colors.accent }}>
                {stats.summary.subscriptionsWithAffiliate}
              </div>
              <div className="text-xs" style={{ color: colors.textMuted }}>Via Affiliate</div>
            </div>
            <div 
              className="p-3 rounded-md text-center"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <div className="text-2xl font-bold" style={{ color: colors.warning }}>
                ${stats.summary.totalMRR}
              </div>
              <div className="text-xs" style={{ color: colors.textMuted }}>Total MRR</div>
            </div>
            <div 
              className="p-3 rounded-md text-center"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <div className="text-2xl font-bold" style={{ color: colors.textSecondary }}>
                {stats.summary.uniqueAffiliates}
              </div>
              <div className="text-xs" style={{ color: colors.textMuted }}>Affiliates</div>
            </div>
          </div>

          {/* Affiliates List */}
          {stats.affiliates.length > 0 ? (
            <div className="space-y-2">
              <h5 className="text-xs font-medium" style={{ color: colors.textSecondary }}>
                Affiliate Breakdown
              </h5>
              {stats.affiliates.map((affiliate) => (
                <div 
                  key={affiliate.code}
                  className="rounded-md overflow-hidden"
                  style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                >
                  {/* Affiliate Header */}
                  <button
                    onClick={() => setExpandedAffiliate(
                      expandedAffiliate === affiliate.code ? null : affiliate.code
                    )}
                    className="w-full p-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
                      >
                        {affiliate.code.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium" style={{ color: colors.textPrimary }}>
                          {affiliate.code}
                        </div>
                        <div className="text-xs" style={{ color: colors.textMuted }}>
                          {affiliate.activeSubscriptions} active · ${affiliate.totalMRR} MRR
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyAffiliateLink(affiliate.code);
                        }}
                        className="p-2 rounded-lg transition-all hover:opacity-70"
                        style={{ backgroundColor: colors.bgSecondary }}
                        title="Copy affiliate link"
                      >
                        {copiedLink === affiliate.code ? (
                          <Check size={14} style={{ color: colors.success }} />
                        ) : (
                          <Copy size={14} style={{ color: colors.textMuted }} />
                        )}
                      </button>
                      <ChevronDown 
                        size={16} 
                        style={{ 
                          color: colors.textMuted,
                          transform: expandedAffiliate === affiliate.code ? 'rotate(180deg)' : 'none',
                          transition: 'transform 200ms'
                        }} 
                      />
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {expandedAffiliate === affiliate.code && (
                    <div 
                      className="px-3 pb-3 space-y-2"
                      style={{ borderTop: `1px solid ${colors.border}` }}
                    >
                      <div className="pt-2 text-xs" style={{ color: colors.textMuted }}>
                        Customers ({affiliate.customers.length})
                      </div>
                      {affiliate.customers.map((customer, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between py-2 px-3 rounded-lg"
                          style={{ backgroundColor: colors.bgSecondary }}
                        >
                          <div>
                            <div className="text-sm" style={{ color: colors.textPrimary }}>
                              {customer.email}
                            </div>
                            <div className="text-xs" style={{ color: colors.textMuted }}>
                              {new Date(customer.created).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div 
                              className="text-sm font-medium"
                              style={{ color: customer.status === 'active' ? colors.success : colors.textMuted }}
                            >
                              ${customer.mrr}/mo
                            </div>
                            <div 
                              className="text-xs"
                              style={{ 
                                color: customer.status === 'active' ? colors.success : colors.error
                              }}
                            >
                              {customer.status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div 
              className="text-center py-6 rounded-md"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <Link2 size={24} style={{ color: colors.textMuted }} className="mx-auto mb-2" />
              <p style={{ color: colors.textMuted }}>No affiliate subscriptions yet</p>
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                Share links like: my-mind.me/?ref=CODE
              </p>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      {!stats && !loading && (
        <div 
          className="p-4 rounded-md text-center"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
        >
          <Link2 size={20} style={{ color: colors.textMuted }} className="mx-auto mb-2" />
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Click "Fetch Stats" to load affiliate data from Stripe
          </p>
          <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
            Affiliate links: my-mind.me/?ref=AFFILIATE_CODE
          </p>
        </div>
      )}
    </div>
  );
};

// Analytics Dashboard Component - calculates stats from loaded users
const AnalyticsDashboard = ({ users, colors }) => {
  if (!users || users.length === 0) return null;
  
  const now = new Date();
  const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
  
  // User metrics
  const totalUsers = users.length;
  const tierCounts = { free: 0, pro: 0, friend: 0, dev: 0 };
  users.forEach(u => {
    const tier = u.tier || 'free';
    if (tierCounts[tier] !== undefined) tierCounts[tier]++;
  });
  
  // Time-based metrics
  const usersThisWeek = users.filter(u => {
    if (!u.createdAt) return false;
    const created = typeof u.createdAt === 'string' ? new Date(u.createdAt) : u.createdAt.toDate?.() || new Date(u.createdAt);
    return created >= oneWeekAgo;
  }).length;
  
  const usersThisMonth = users.filter(u => {
    if (!u.createdAt) return false;
    const created = typeof u.createdAt === 'string' ? new Date(u.createdAt) : u.createdAt.toDate?.() || new Date(u.createdAt);
    return created >= oneMonthAgo;
  }).length;
  
  // Feature adoption
  const withHabits = users.filter(u => u.habits?.length > 0).length;
  const withTasks = users.filter(u => u.tasks?.length > 0).length;
  const withGoals = users.filter(u => u.goals?.length > 0).length;
  const withEvents = users.filter(u => u.events?.length > 0).length;
  const withGoogleCal = users.filter(u => u.googleCalendar?.connected).length;
  const withPomodoro = users.filter(u => u.pomodoroSettings).length;
  
  // Aggregate stats
  const totalHabits = users.reduce((sum, u) => sum + (u.habits?.length || 0), 0);
  const totalTasks = users.reduce((sum, u) => sum + (u.tasks?.length || 0), 0);
  const totalGoals = users.reduce((sum, u) => sum + (u.goals?.length || 0), 0);
  const totalEvents = users.reduce((sum, u) => sum + (u.events?.length || 0), 0);
  const totalArchivedTasks = users.reduce((sum, u) => sum + (u.archivedTasks?.length || 0), 0);
  const totalArchivedGoals = users.reduce((sum, u) => sum + (u.archivedGoals?.length || 0), 0);
  
  // Streak stats
  const allStreaks = users.flatMap(u => (u.habits || []).map(h => h.currentStreak || h.streak || 0));
  const maxStreak = allStreaks.length > 0 ? Math.max(...allStreaks) : 0;
  const avgStreak = allStreaks.length > 0 ? (allStreaks.reduce((a, b) => a + b, 0) / allStreaks.length).toFixed(1) : 0;
  const usersWithWeekStreak = users.filter(u => (u.habits || []).some(h => (h.currentStreak || h.streak || 0) >= 7)).length;
  
  // Active users (have completions in last 7 days)
  const activeUsers = users.filter(u => {
    if (!u.completions) return false;
    return Object.keys(u.completions).some(dateStr => {
      const date = new Date(dateStr);
      return date >= oneWeekAgo;
    });
  }).length;
  
  // Subscription metrics
  const proUsers = tierCounts.pro;
  const conversionRate = totalUsers > 0 ? ((proUsers / totalUsers) * 100).toFixed(1) : 0;
  const expiringThisWeek = users.filter(u => {
    if (!u.subscriptionEnd) return false;
    const end = typeof u.subscriptionEnd === 'string' ? new Date(u.subscriptionEnd) : u.subscriptionEnd.toDate?.() || new Date(u.subscriptionEnd);
    return end >= now && end <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  }).length;
  
  // Top users by habit count
  const topUsers = [...users]
    .map(u => ({ email: u.email, habits: u.habits?.length || 0, tasks: u.tasks?.length || 0 }))
    .sort((a, b) => (b.habits + b.tasks) - (a.habits + a.tasks))
    .slice(0, 5);
  
  const pct = (n, total) => total > 0 ? `${((n / total) * 100).toFixed(0)}%` : '0%';
  
  const StatCell = ({ label, value, subValue, accent }) => (
    <div className="text-center p-2">
      <div className="text-lg font-bold" style={{ color: accent ? colors.accent : colors.textPrimary }}>{value}</div>
      <div className="text-xs" style={{ color: colors.textMuted }}>{label}</div>
      {subValue && <div className="text-xs" style={{ color: colors.textSecondary }}>{subValue}</div>}
    </div>
  );
  
  const ProgressBar = ({ value, max, color }) => (
    <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: colors.border }}>
      <div 
        className="h-full rounded-full transition-all" 
        style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, backgroundColor: color || colors.accent }}
      />
    </div>
  );
  
  return (
    <div 
      className="rounded-md mb-6 overflow-hidden"
      style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
    >
      {/* Header */}
      <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <span className="text-sm font-medium flex items-center gap-2" style={{ color: colors.textSecondary }}>
          <TrendingUp size={14} />
          Analytics
        </span>
        <span className="text-xs" style={{ color: colors.textMuted }}>
          {totalUsers} total users
        </span>
      </div>
      
      <div className="p-4 grid gap-4">
        {/* Row 1: User Stats & Tiers */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* User Growth */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>USER GROWTH</div>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div>
                <div className="text-sm font-bold" style={{ color: colors.success }}>+{usersThisWeek}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>7d</div>
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: colors.accent }}>+{usersThisMonth}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>30d</div>
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{totalUsers}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>total</div>
              </div>
            </div>
          </div>
          
          {/* Tiers Breakdown */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>TIERS</div>
            <div className="grid grid-cols-4 gap-1 text-center text-xs">
              <div>
                <div className="font-bold" style={{ color: colors.textSecondary }}>{tierCounts.free}</div>
                <div style={{ color: colors.textMuted }}>Free</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.accent }}>{tierCounts.pro}</div>
                <div style={{ color: colors.textMuted }}>Pro</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.warning }}>{tierCounts.friend}</div>
                <div style={{ color: colors.textMuted }}>Friend</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.success }}>{tierCounts.dev}</div>
                <div style={{ color: colors.textMuted }}>Dev</div>
              </div>
            </div>
          </div>
          
          {/* Engagement */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>ENGAGEMENT</div>
            <div className="grid grid-cols-2 gap-1 text-center">
              <div>
                <div className="text-sm font-bold" style={{ color: colors.accent }}>{activeUsers}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>active 7d</div>
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{pct(activeUsers, totalUsers)}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>rate</div>
              </div>
            </div>
          </div>
          
          {/* Revenue */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>REVENUE</div>
            <div className="grid grid-cols-2 gap-1 text-center">
              <div>
                <div className="text-sm font-bold" style={{ color: colors.accent }}>{conversionRate}%</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>conv.</div>
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: expiringThisWeek > 0 ? colors.warning : colors.textSecondary }}>{expiringThisWeek}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>expiring</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Row 2: Feature Adoption */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
          <div className="text-xs font-medium mb-3" style={{ color: colors.textMuted }}>FEATURE ADOPTION</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-xs">
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>Habits</span>
                <span style={{ color: colors.textPrimary }}>{withHabits}/{totalUsers}</span>
              </div>
              <ProgressBar value={withHabits} max={totalUsers} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>Tasks</span>
                <span style={{ color: colors.textPrimary }}>{withTasks}/{totalUsers}</span>
              </div>
              <ProgressBar value={withTasks} max={totalUsers} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>Goals</span>
                <span style={{ color: colors.textPrimary }}>{withGoals}/{totalUsers}</span>
              </div>
              <ProgressBar value={withGoals} max={totalUsers} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>Events</span>
                <span style={{ color: colors.textPrimary }}>{withEvents}/{totalUsers}</span>
              </div>
              <ProgressBar value={withEvents} max={totalUsers} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>GCal</span>
                <span style={{ color: colors.textPrimary }}>{withGoogleCal}/{totalUsers}</span>
              </div>
              <ProgressBar value={withGoogleCal} max={totalUsers} color={colors.success} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span style={{ color: colors.textSecondary }}>Pomodoro</span>
                <span style={{ color: colors.textPrimary }}>{withPomodoro}/{totalUsers}</span>
              </div>
              <ProgressBar value={withPomodoro} max={totalUsers} color={colors.warning} />
            </div>
          </div>
        </div>
        
        {/* Row 3: Content & Streaks */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Content Totals */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>CONTENT TOTALS</div>
            <div className="grid grid-cols-4 gap-1 text-center text-xs">
              <div>
                <div className="font-bold" style={{ color: colors.textPrimary }}>{totalHabits}</div>
                <div style={{ color: colors.textMuted }}>habits</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.textPrimary }}>{totalTasks}</div>
                <div style={{ color: colors.textMuted }}>tasks</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.textPrimary }}>{totalGoals}</div>
                <div style={{ color: colors.textMuted }}>goals</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.textPrimary }}>{totalEvents}</div>
                <div style={{ color: colors.textMuted }}>events</div>
              </div>
            </div>
          </div>
          
          {/* Archived/Completed */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>COMPLETED</div>
            <div className="grid grid-cols-2 gap-1 text-center">
              <div>
                <div className="text-sm font-bold" style={{ color: colors.success }}>{totalArchivedTasks}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>tasks done</div>
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: colors.success }}>{totalArchivedGoals}</div>
                <div className="text-xs" style={{ color: colors.textMuted }}>goals done</div>
              </div>
            </div>
          </div>
          
          {/* Streaks */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>STREAKS</div>
            <div className="grid grid-cols-3 gap-1 text-center text-xs">
              <div>
                <div className="font-bold" style={{ color: colors.accent }}>{maxStreak}</div>
                <div style={{ color: colors.textMuted }}>max</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.textPrimary }}>{avgStreak}</div>
                <div style={{ color: colors.textMuted }}>avg</div>
              </div>
              <div>
                <div className="font-bold" style={{ color: colors.success }}>{usersWithWeekStreak}</div>
                <div style={{ color: colors.textMuted }}>7d+</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Row 4: Top Users */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: colors.bg }}>
          <div className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>TOP ENGAGED USERS</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
            {topUsers.map((u, i) => (
              <div 
                key={i} 
                className="flex items-center justify-between p-2 rounded"
                style={{ backgroundColor: colors.bgSecondary }}
              >
                <span className="truncate" style={{ color: colors.textSecondary }} title={u.email}>
                  {u.email?.split('@')[0] || 'Unknown'}
                </span>
                <span className="font-medium ml-2" style={{ color: colors.accent }}>
                  {u.habits + u.tasks}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Tier badge component
const TierBadge = ({ tier, colors }) => {
  const config = getTierConfig(tier);
  const tierStyles = {
    free: { bg: colors.bgSecondary, text: colors.textSecondary, icon: User },
    pro: { bg: `${colors.accent}20`, text: colors.accent, icon: Crown },
    friend: { bg: `${colors.warning}20`, text: colors.warning, icon: Heart },
    dev: { bg: `${colors.success}20`, text: colors.success, icon: Code },
  };
  
  const style = tierStyles[tier] || tierStyles.free;
  const Icon = style.icon;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      <Icon size={12} />
      {config.name}
    </span>
  );
};

// Compact user table component for admin dashboard
const UsersTable = ({ users, colors, onUpdateTier, updatingUid, selectedUsers, onToggleSelect, onSelectAll }) => {
  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;
  const tiers = getAllTierIds();
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr style={{ backgroundColor: colors.bg }}>
            <th className="py-2 px-2 text-left w-8" style={{ borderBottom: `1px solid ${colors.border}` }}>
              <button
                onClick={onSelectAll}
                className="p-1 rounded hover:opacity-80 transition-opacity"
                style={{ color: colors.textMuted }}
              >
                {allSelected ? (
                  <CheckSquare size={16} style={{ color: colors.accent }} />
                ) : someSelected ? (
                  <Minus size={16} style={{ color: colors.accent }} />
                ) : (
                  <Square size={16} />
                )}
              </button>
            </th>
            <th className="py-2 px-2 text-left font-medium" style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
              Email
            </th>
            <th className="py-2 px-2 text-left font-medium w-28" style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
              Tier
            </th>
            <th className="py-2 px-2 text-left font-medium w-40" style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
              Change Tier
            </th>
            <th className="py-2 px-2 text-center font-medium w-16" style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((userData, index) => (
            <UserTableRow
              key={userData.uid}
              userData={userData}
              colors={colors}
              onUpdateTier={onUpdateTier}
              updating={updatingUid === userData.uid}
              tiers={tiers}
              isSelected={selectedUsers.includes(userData.uid)}
              onToggleSelect={() => onToggleSelect(userData.uid)}
              isEven={index % 2 === 0}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Single row in the users table
const UserTableRow = ({ userData, colors, onUpdateTier, updating, tiers, isSelected, onToggleSelect, isEven }) => {
  const [selectedTier, setSelectedTier] = useState(userData.tier || 'free');
  const hasChanged = selectedTier !== (userData.tier || 'free');
  
  // Sync local state when userData.tier changes (after save)
  useEffect(() => {
    setSelectedTier(userData.tier || 'free');
  }, [userData.tier]);
  
  return (
    <tr 
      style={{ 
        backgroundColor: isSelected ? `${colors.accent}15` : isEven ? colors.bgSecondary : colors.bg 
      }}
    >
      <td className="py-2 px-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <button
          onClick={onToggleSelect}
          className="p-1 rounded hover:opacity-80 transition-opacity"
          style={{ color: colors.textMuted }}
        >
          {isSelected ? (
            <CheckSquare size={16} style={{ color: colors.accent }} />
          ) : (
            <Square size={16} />
          )}
        </button>
      </td>
      <td className="py-2 px-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <div className="flex flex-col">
          <span 
            className="font-medium truncate max-w-xs"
            style={{ color: colors.textPrimary }}
            title={userData.email}
          >
            {userData.email || 'No email'}
          </span>
          <span 
            className="text-xs truncate max-w-xs"
            style={{ color: colors.textMuted }}
            title={userData.uid}
          >
            {userData.uid}
          </span>
        </div>
      </td>
      <td className="py-2 px-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <TierBadge tier={userData.tier || 'free'} colors={colors} />
      </td>
      <td className="py-2 px-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          className="w-full py-1 px-2 rounded text-sm outline-none cursor-pointer"
          style={{ 
            backgroundColor: colors.bg,
            border: `1px solid ${hasChanged ? colors.accent : colors.border}`,
            color: colors.textPrimary
          }}
        >
          {tiers.map((tierId) => (
            <option key={tierId} value={tierId}>
              {getTierConfig(tierId).name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 px-2 text-center" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <button
          onClick={() => onUpdateTier(userData.uid, selectedTier)}
          disabled={!hasChanged || updating}
          className="px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 mx-auto"
          style={{ 
            backgroundColor: hasChanged ? colors.accent : 'transparent',
            color: hasChanged ? colors.bg : colors.textMuted,
            opacity: updating ? 0.7 : 1,
            cursor: hasChanged && !updating ? 'pointer' : 'not-allowed'
          }}
        >
          {updating ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
          Save
        </button>
      </td>
    </tr>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { isDark, user } = useApp();
  const { isDev, tier } = useSubscription();
  const colors = getColors(isDark);
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email'); // 'email' or 'uid'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [updatingUid, setUpdatingUid] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recentUsers, setRecentUsers] = useState([]);
  
  // Selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkTier, setBulkTier] = useState('free');
  
  // Redirect if not dev
  useEffect(() => {
    if (tier && tier !== 'dev') {
      navigate('/dashboard');
    }
  }, [tier, navigate]);
  
  // Load all users on mount
  useEffect(() => {
    loadAllUsers();
  }, []);
  
  const loadAllUsers = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      // Load all users (no limit)
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const usersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      
      setRecentUsers(usersData);
      setSelectedUsers([]);
    } catch (err) {
      devError('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  // Selection handlers
  const handleToggleSelect = (uid) => {
    setSelectedUsers(prev => 
      prev.includes(uid) 
        ? prev.filter(id => id !== uid)
        : [...prev, uid]
    );
  };
  
  const handleSelectAll = () => {
    const allUids = recentUsers.map(u => u.uid);
    const allSelected = allUids.length > 0 && allUids.every(uid => selectedUsers.includes(uid));
    
    if (allSelected) {
      // Deselect all
      setSelectedUsers([]);
    } else {
      // Select all
      setSelectedUsers(allUids);
    }
  };
  
  // Bulk update handler
  const handleBulkUpdateTier = async () => {
    if (selectedUsers.length === 0) return;
    
    if (!window.confirm(`Update ${selectedUsers.length} users to "${getTierConfig(bulkTier).name}" tier?`)) {
      return;
    }
    
    setBulkUpdating(true);
    setError('');
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const uid of selectedUsers) {
        try {
          await setDoc(doc(db, 'users', uid), {
            tier: bulkTier,
            tierUpdatedAt: new Date().toISOString(),
            tierUpdatedBy: user.userId
          }, { merge: true });
          successCount++;
        } catch (err) {
          devError(`Failed to update user ${uid}:`, err);
          failCount++;
        }
      }
      
      // Update local state instead of reloading
      setRecentUsers(prev => prev.map(u => 
        selectedUsers.includes(u.uid) ? { ...u, tier: bulkTier } : u
      ));
      
      if (failCount === 0) {
        setSuccess(`Successfully updated ${successCount} users to ${getTierConfig(bulkTier).name}`);
      } else {
        setError(`Updated ${successCount} users, ${failCount} failed`);
      }
      
      setSelectedUsers([]);
    } catch (err) {
      devError('Bulk update error:', err);
      setError('Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }
    
    setSearching(true);
    setError('');
    setUsers([]);
    
    try {
      if (searchType === 'uid') {
        // Direct document lookup by UID
        const userDoc = await getDoc(doc(db, 'users', searchQuery.trim()));
        if (userDoc.exists()) {
          setUsers([{ uid: userDoc.id, ...userDoc.data() }]);
        } else {
          setError('No user found with that UID');
        }
      } else {
        // Search by email
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', searchQuery.trim().toLowerCase()));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          setError('No user found with that email');
        } else {
          const usersData = snapshot.docs.map(doc => ({
            uid: doc.id,
            ...doc.data()
          }));
          setUsers(usersData);
        }
      }
    } catch (err) {
      devError('Error searching:', err);
      setError('Search failed. Check Firestore indexes.');
    } finally {
      setSearching(false);
    }
  };
  
  const handleUpdateTier = async (uid, newTier) => {
    setUpdatingUid(uid);
    setError('');
    setSuccess('');
    
    try {
      await setDoc(doc(db, 'users', uid), {
        tier: newTier,
        tierUpdatedAt: new Date(),
        tierUpdatedBy: user.userId,
      }, { merge: true });
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.uid === uid ? { ...u, tier: newTier } : u
      ));
      setRecentUsers(prev => prev.map(u => 
        u.uid === uid ? { ...u, tier: newTier } : u
      ));
      
      setSuccess(`Updated user ${uid} to ${getTierConfig(newTier).name}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      devError('Error updating tier:', err);
      setError(`Failed to update tier: ${err.message}`);
    } finally {
      setUpdatingUid(null);
    }
  };
  
  // Don't render if not dev
  if (!isDev) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.bg }}
      >
        <div className="text-center">
          <Shield size={48} style={{ color: colors.textMuted }} className="mx-auto mb-4" />
          <h2 style={{ color: colors.textPrimary }} className="text-xl font-semibold mb-2">
            Access Denied
          </h2>
          <p style={{ color: colors.textSecondary }}>
            This page is only accessible to developers.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.bg }}
    >
      <div className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg transition-all hover:opacity-80"
            style={{ 
              backgroundColor: colors.bgSecondary,
              color: colors.textPrimary
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Shield size={24} style={{ color: colors.accent }} />
              <h1 
                className="text-2xl font-bold"
                style={{ color: colors.textPrimary, fontFamily: fonts.heading }}
              >
                Admin Panel
              </h1>
            </div>
            <p style={{ color: colors.textSecondary }} className="text-sm mt-1">
              Manage user tiers and access levels
            </p>
          </div>
        </div>
        
        {/* Analytics Dashboard */}
        <AnalyticsDashboard users={recentUsers} colors={colors} />
        
        {/* Search Section */}
        <div 
          className="p-6 rounded-lg mb-6"
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          <h2 
            className="text-lg font-semibold mb-4 flex items-center gap-2"
            style={{ color: colors.textPrimary }}
          >
            <Search size={20} />
            Find User
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Type Toggle */}
            <div 
              className="flex rounded-lg overflow-hidden"
              style={{ border: `1px solid ${colors.border}` }}
            >
              <button
                onClick={() => setSearchType('email')}
                className="px-4 py-2 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: searchType === 'email' ? colors.accent : 'transparent',
                  color: searchType === 'email' ? colors.bg : colors.textSecondary
                }}
              >
                Email
              </button>
              <button
                onClick={() => setSearchType('uid')}
                className="px-4 py-2 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: searchType === 'uid' ? colors.accent : 'transparent',
                  color: searchType === 'uid' ? colors.bg : colors.textSecondary
                }}
              >
                UID
              </button>
            </div>
            
            {/* Search Input */}
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                placeholder={searchType === 'email' ? 'user@example.com' : 'Firebase UID...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 px-4 py-2 rounded-lg text-sm"
                style={{ 
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.border}`,
                  color: colors.textPrimary
                }}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                style={{ 
                  backgroundColor: colors.accent,
                  color: colors.onAccent,
                  opacity: searching ? 0.7 : 1
                }}
              >
                {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                Search
              </button>
            </div>
          </div>
        </div>
        
        {/* Messages */}
        {error && (
          <div 
            className="p-4 rounded-md mb-4 flex items-center gap-2"
            style={{ 
              backgroundColor: `${colors.error}15`,
              border: `1px solid ${colors.error}30`,
              color: colors.error
            }}
          >
            <AlertTriangle size={18} />
            {error}
          </div>
        )}
        
        {success && (
          <div 
            className="p-4 rounded-md mb-4 flex items-center gap-2"
            style={{ 
              backgroundColor: `${colors.success}15`,
              border: `1px solid ${colors.success}30`,
              color: colors.success
            }}
          >
            <Check size={18} />
            {success}
          </div>
        )}
        
        {/* Search Results */}
        {users.length > 0 && (
          <div 
            className="mb-8 rounded-md overflow-hidden"
            style={{ 
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <div 
              className="p-3"
              style={{ borderBottom: `1px solid ${colors.border}` }}
            >
              <h3 
                className="text-sm font-medium flex items-center gap-2"
                style={{ color: colors.textSecondary }}
              >
                <Search size={16} />
                Search Results ({users.length})
              </h3>
            </div>
            <UsersTable
              users={users}
              colors={colors}
              onUpdateTier={handleUpdateTier}
              updatingUid={updatingUid}
              selectedUsers={[]}
              onToggleSelect={() => {}}
              onSelectAll={() => {}}
            />
          </div>
        )}
        
        {/* Recent Users */}
        <div 
          className="rounded-md overflow-hidden"
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3"
            style={{ borderBottom: `1px solid ${colors.border}` }}
          >
            <h3 
              className="text-sm font-medium flex items-center gap-2"
              style={{ color: colors.textSecondary }}
            >
              <Users size={16} />
              All Users ({recentUsers.length})
            </h3>
            <button
              onClick={loadAllUsers}
              disabled={loading}
              className="p-2 rounded-lg transition-all hover:opacity-80"
              style={{ 
                backgroundColor: colors.bg,
                color: colors.textMuted
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
          
          {/* Bulk Actions Bar */}
          {selectedUsers.length > 0 && (
            <div 
              className="flex items-center gap-3 p-3 text-sm"
              style={{ 
                backgroundColor: `${colors.accent}10`,
                borderBottom: `1px solid ${colors.border}`
              }}
            >
              <span style={{ color: colors.textPrimary }}>
                {selectedUsers.length} selected
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <select
                  value={bulkTier}
                  onChange={(e) => setBulkTier(e.target.value)}
                  className="py-1 px-2 rounded text-sm outline-none"
                  style={{ 
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    color: colors.textPrimary
                  }}
                >
                  {getAllTierIds().map((tierId) => (
                    <option key={tierId} value={tierId}>
                      {getTierConfig(tierId).name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleBulkUpdateTier}
                  disabled={bulkUpdating}
                  className="px-3 py-1 rounded text-sm font-medium transition-all flex items-center gap-1"
                  style={{ 
                    backgroundColor: colors.accent,
                    color: colors.onAccent
                  }}
                >
                  {bulkUpdating ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                  Apply to Selected
                </button>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="px-2 py-1 rounded text-sm transition-all"
                  style={{ color: colors.textMuted }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          
          {/* Scrollable Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader size={24} className="animate-spin" style={{ color: colors.accent }} />
            </div>
          ) : recentUsers.length > 0 ? (
            <div 
              style={{ 
                maxHeight: '600px', // ~15 rows at ~40px each
                overflowY: 'auto'
              }}
            >
              <UsersTable
                users={recentUsers}
                colors={colors}
                onUpdateTier={handleUpdateTier}
                updatingUid={updatingUid}
                selectedUsers={selectedUsers}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAll}
              />
            </div>
          ) : (
            <div 
              className="text-center py-12"
              style={{ color: colors.textMuted }}
            >
              No users found
            </div>
          )}
        </div>
        
        {/* Developer Tools Section - Compact */}
        <div 
          className="mt-8 p-4 rounded-lg"
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          <h2 
            className="text-sm font-semibold mb-3 flex items-center gap-2"
            style={{ color: colors.textPrimary }}
          >
            <Zap size={16} style={{ color: colors.accent }} />
            Developer Tools
          </h2>
          
          <DevToolsPanel colors={colors} user={user} />
          
          {/* Affiliate Stats - kept separate as it has expandable content */}
          <div className="pt-3 mt-3 border-t" style={{ borderColor: colors.border }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium flex items-center gap-1" style={{ color: colors.textSecondary }}>
                <Link2 size={12} /> Affiliates
              </span>
            </div>
            <AffiliateStatsPanel colors={colors} />
          </div>
        </div>
        
        {/* Tier Legend */}
        <div 
          className="mt-8 p-4 rounded-md"
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          <h4 
            className="text-sm font-medium mb-3"
            style={{ color: colors.textSecondary }}
          >
            Tier Legend
          </h4>
          <div className="flex flex-wrap gap-4">
            {getAllTierIds().map((tierId) => {
              const config = getTierConfig(tierId);
              return (
                <div key={tierId} className="flex items-center gap-2">
                  <TierBadge tier={tierId} colors={colors} />
                  <span 
                    className="text-xs"
                    style={{ color: colors.textMuted }}
                  >
                    {config.description}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminPage;
